import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, MenuItem, TextField } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { useFormik } from 'formik';
import { object, string } from 'yup';

import {
  preparePersonnelInfoRequest,
  preparePersonnelInfoState
} from 'components/pages/study/StudyDetails/SiteDetails/AddOrEditPersonnelModal/utils';

import UserApiApi from '../../../../../../../api/patient/UserApiApi';
import NotificationManager from '../../../../../../../common/notifications/NotificationManager';
import { YEAR_DASH_MONTH_DASH_DAY } from '../../../../../../../constants/dateFormat';
import { DATA_SAVED, SOMETHING_WENT_WRONG } from '../../../../../../../constants/notificationMessages';
import { NS_COMMON, NSPS_SITE } from '../../../../../../../services/i18n/namespaces';

import './PersonalInformation.scss';

const languages = [
  { id: 'EN', name: 'English' },
  { id: 'AR', name: 'Arabic' },
  { id: 'BG', name: 'Bulgarian' },
  { id: 'HR', name: 'Croatian' },
  { id: 'CS', name: 'Czech' },
  { id: 'DA', name: 'Danish' },
  { id: 'NL', name: 'Dutch' },
  { id: 'FR', name: 'French' },
  { id: 'DE', name: 'German' },
  { id: 'EL', name: 'Greek' },
  { id: 'HI', name: 'Hindi' },
  { id: 'HU', name: 'Hungarian' },
  { id: 'IT', name: 'Italian' },
  { id: 'NO', name: 'Norwegian' },
  { id: 'PL', name: 'Polish' },
  { id: 'PT', name: 'Portuguese' },
  { id: 'RO', name: 'Romanian' },
  { id: 'ES', name: 'Spanish' },
  { id: 'SV', name: 'Swedish' },
  { id: 'TR', name: 'Turkish' }
];

const validationSchema = object({
  firstName: string()
    .trim()
    .required('First name is required'),
  lastName: string()
    .trim()
    .required('Last name is required'),
  email: string()
    .trim()
    .required('Email is required')
    .email('Email is not valid')
});

export default function PersonalInformation({ ssuId, personnelInfo, noActiveRole, setPersonnelInfo, setRoles }) {
  const { t } = useTranslation();

  const { id: personnelId, firstName, lastName, email, startDate, preferredLanguage } = personnelInfo;

  const addOrUpdatePersonnel = personnelInfo => {
    UserApiApi.addOrUpdatePersonnel(ssuId, preparePersonnelInfoRequest(personnelInfo))
      .then(({ data }) => {
        NotificationManager.success(DATA_SAVED);
        setPersonnelInfo(preparePersonnelInfoState(data.personnelInfo));
        setRoles(data?.roles);
      })
      .catch(err => {
        if (err.response.status === 400 || err.response.status === 404) {
          NotificationManager.error(err.response.data.message);
        } else {
          NotificationManager.error(SOMETHING_WENT_WRONG);
        }
      });
  };

  const { values, errors, touched, handleChange, handleBlur, handleSubmit } = useFormik({
    initialValues: { firstName, lastName, email },
    enableReinitialize: true,
    validationSchema,
    onSubmit: values => {
      const updatedPersonnelInfo = { ...personnelInfo, ...values };
      setPersonnelInfo(updatedPersonnelInfo);
      addOrUpdatePersonnel(updatedPersonnelInfo);
    }
  });

  const handlePreferredLanguageChange = event => {
    setPersonnelInfo({ ...personnelInfo, preferredLanguage: event.target.value });
  };

  const handleStartDateChange = value => {
    setPersonnelInfo({ ...personnelInfo, startDate: value });
  };

  const handleResetPassword = () => {
    UserApiApi.resetPassword(ssuId, personnelId)
      .then(() => {
        NotificationManager.success(
          t('addOrEditPersonnel.personalInformation.resetPasswordSuccess', { ns: NSPS_SITE })
        );
      })
      .catch(err => {
        if (err.response.status === 404) {
          NotificationManager.error(err.response.data.message);
        } else {
          NotificationManager.error(SOMETHING_WENT_WRONG);
        }
      });
  };

  const handleResendInvitation = () => {
    UserApiApi.resendInvitation(ssuId, personnelId)
      .then(() => {
        NotificationManager.success(
          t('addOrEditPersonnel.personalInformation.resendInvitationSuccess', { ns: NSPS_SITE })
        );
      })
      .catch(err => {
        const message = err?.response?.data?.message;
        if (message) {
          if (err.response.status === 404) {
            NotificationManager.error(message);
          } else if (message.includes('User has already accepted the invitation')) {
            NotificationManager.error('User has already accepted the invitation');
          } else {
            NotificationManager.error(SOMETHING_WENT_WRONG);
          }
        }
      });
  };

  const resetAndInviteButtonsDisabled = !personnelId || noActiveRole;

  return (
    <div className="add-or-edit-personnel-modal-section">
      <div className="add-or-edit-personnel-modal-section-header">
        <h5 className="add-or-edit-personnel-modal-section-title">
          {t('addOrEditPersonnel.personalInformation.title', { ns: NSPS_SITE })}
        </h5>
      </div>
      <div className="add-or-edit-personnel-modal-section-body">
        <form onSubmit={handleSubmit}>
          <div className="add-or-edit-personnel-modal-fields-grid">
            <div className="add-or-edit-personnel-modal-fields-row">
              <TextField
                label={t('addOrEditPersonnel.personalInformation.firstName', { ns: NSPS_SITE })}
                variant="outlined"
                className="add-or-edit-personnel-modal-field"
                name="firstName"
                value={values.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.firstName && Boolean(errors.firstName)}
                helperText={touched.firstName && errors.firstName}
              />
              <TextField
                label={t('addOrEditPersonnel.personalInformation.lastName', { ns: NSPS_SITE })}
                variant="outlined"
                className="add-or-edit-personnel-modal-field"
                name="lastName"
                value={values.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.lastName && Boolean(errors.lastName)}
                helperText={touched.lastName && errors.lastName}
              />
            </div>
            <TextField
              label={t('email', { ns: NS_COMMON })}
              type="email"
              variant="outlined"
              className="add-or-edit-personnel-modal-field"
              name="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && Boolean(errors.email)}
              helperText={touched.email && errors.email}
              disabled
              slotProps={{
                inputLabel: {
                  shrink: true
                }
              }}
            />
            <TextField
              label={t('addOrEditPersonnel.personalInformation.preferredLanguage', { ns: NSPS_SITE })}
              value={preferredLanguage}
              onChange={handlePreferredLanguageChange}
              variant="outlined"
              select
              className="add-or-edit-personnel-modal-field add-or-edit-personnel-modal-field--language"
            >
              {languages.map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
            <DesktopDatePicker
              label={t('startDate', { ns: NS_COMMON })}
              value={startDate}
              onChange={handleStartDateChange}
              format={YEAR_DASH_MONTH_DASH_DAY}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  className: 'add-or-edit-personnel-modal-field'
                },
                actionBar: {
                  actions: ['clear']
                }
              }}
            />
            <div className="add-or-edit-personnel-modal-actions">
              <Button type="submit" variant="contained" disableElevation>
                {t('save', { ns: NS_COMMON })}
              </Button>
              <Button
                variant="contained"
                disableElevation
                disabled={resetAndInviteButtonsDisabled}
                onClick={handleResetPassword}
              >
                {t('addOrEditPersonnel.personalInformation.resetPassword', { ns: NSPS_SITE })}
              </Button>
              <Button
                variant="contained"
                disableElevation
                disabled={resetAndInviteButtonsDisabled}
                onClick={handleResendInvitation}
              >
                {t('addOrEditPersonnel.personalInformation.resendInvitation', { ns: NSPS_SITE })}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
